import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";

const OuterContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 500px;
`;

const TitleHeader = styled.div`
  background-color: #195174;
  padding: 10px;
  border-radius: 5px 5px 0 0;
`;

const Title = styled.h3`
  color: #fff;
  margin: 0;
`;

const ResetFormContainer = styled.div`
  background: #fff;
  padding: 20px;
  border-radius: 0 0 5px 5px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
`;

const InstructionText = styled.p`
  font-size: 0.9rem;
  color: #195174;
`;

const Label = styled.label`
  display: block;
  margin-top: 1em;
  margin-bottom: 0.3em;
  color: #195174;
  font-weight: bold;
`;

const InputContainer = styled.div`
  position: relative;
`;

const TextInput = styled.input`
  width: 90%;
  padding: 8px 35px 8px 10px;
  font-size: 1rem;
  border: 1px solid #195174;
  border-radius: 4px;
`;

const ToggleVisibilityButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #195174;
`;

const PasswordStrength = styled.div`
  margin-top: 0.5em;
  font-size: 0.85em;
  color: ${({ valid }) => (valid ? "green" : "red")};
  & + div { margin-top: 0.25em; }
`;

const ButtonBar = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 1em;
`;

const Button = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  font-weight: bold;
  &.cancel {
    background: #ccc;
    color: #333;
  }
  &.reset {
    background: #195174;
    color: #fff;
    &:disabled { background: #888; cursor: not-allowed; }
  }
`;

export default function AdminResetPassword({
  teacher,
  colors,
  onCancel,
  onComplete
}) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showStrength, setShowStrength] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState({ show: false, title: "", message: "", status: "Success" });

  // strength checks
  const passLength = password.length >= 8;
  const hasLower  = /[a-z]/.test(password);
  const hasUpper  = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial= /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const passwordsMatch = password === confirmPassword;

  const canReset =
    passLength && hasLower && hasUpper && hasNumber && hasSpecial && passwordsMatch;

  const handleResetClick = () => setConfirmOpen(true);
  const handleConfirm = async () => {
    setConfirmOpen(false);
    try {
      const resp = await API.resetUserPassword({
        user_id: teacher.user_id,
        password: password,
        c_password: confirmPassword,
        email: teacher.email,
        name: teacher.name,
      });
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setResult({
        show: true,
        title: status === "Failed" ? "Error" : "Success",
        message: resp.message || (status === "Failed" ? "Reset failed." : "Password has been reset."),
        status
      });
    } catch (err) {
      setResult({
        show: true,
        title: "Error",
        message: err.message || "Reset failed.",
        status: "Failed"
      });
    }
  };
  const finish = () => {
    setResult(r => ({ ...r, show: false, status: "Success" }));
    onComplete();
  };

  return (
    <OuterContainer>
      <TitleHeader>
        <Title>Reset Password for {teacher.email}</Title>
      </TitleHeader>
      <ResetFormContainer>
        <InstructionText>
          Enter a new password (min 8 chars, uppercase, lowercase, number &amp; special).
        </InstructionText>

        <Label>New Password</Label>
        <InputContainer>
          <TextInput
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              setShowStrength(true);
            }}
          />
          <ToggleVisibilityButton onClick={()=>setShowPassword(v=>!v)}>
            {showPassword ? <FaEyeSlash/> : <FaEye/>}
          </ToggleVisibilityButton>
        </InputContainer>

        {showStrength && (
          <>
            <PasswordStrength valid={passLength}>
              • At least 8 characters
            </PasswordStrength>
            <PasswordStrength valid={hasLower}>
              • One lowercase letter
            </PasswordStrength>
            <PasswordStrength valid={hasUpper}>
              • One uppercase letter
            </PasswordStrength>
            <PasswordStrength valid={hasNumber}>
              • One number
            </PasswordStrength>
            <PasswordStrength valid={hasSpecial}>
              • One special character
            </PasswordStrength>
          </>
        )}

        <Label>Confirm Password</Label>
        <InputContainer>
          <TextInput
            type={showConfirm ? "text" : "password"}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
          />
          <ToggleVisibilityButton onClick={()=>setShowConfirm(v=>!v)}>
            {showConfirm ? <FaEyeSlash/> : <FaEye/>}
          </ToggleVisibilityButton>
        </InputContainer>

        {!passwordsMatch && confirmPassword && (
          <InstructionText style={{ color: "red" }}>
            Passwords do not match.
          </InstructionText>
        )}

        <ButtonBar>
          <Button className="cancel" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className="reset"
            disabled={!canReset}
            onClick={handleResetClick}
          >
            Reset Password
          </Button>
        </ButtonBar>
      </ResetFormContainer>

      <ConfirmationModal
        show={confirmOpen}
        title="Confirm Reset"
        message={`Reset password for ${teacher.email}?`}
        onCancel={()=>setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      <ConfirmationModal
        show={result.show}
        title={result.title}
        message={result.message}
        status={result.status}
        single
        onCancel={finish}
        onConfirm={finish}
      />
    </OuterContainer>
  );
}
